import React, { useEffect, useState } from "react";
import API from "../services/api";

// ── Design tokens ────────────────────────────────────────────────────────────
const C = {
  navy:      "var(--primary-color)",
  copper:    "var(--accent-color)",
  red:       "var(--error-color)",
  bg:        "#f4f6f9",
  white:     "#ffffff",
  border:    "#e0e4ea",
  text:      "#1a1f2e",
  muted:     "var(--text-muted)",
  shadow:    "0 2px 8px rgba(0,0,0,0.09)",
};

const CAT_PALETTE = [
  { bg: "#e8f4fd", color: "#1a6fa8" },
  { bg: "#fef3e2", color: "#a16207" },
  { bg: "#f0fdf4", color: "#15803d" },
  { bg: "#fdf4ff", color: "#7c3aed" },
  { bg: "#fff1f2", color: "#be123c" },
  { bg: "#f0f9ff", color: "#0369a1" },
  { bg: "#fef9e7", color: "#854d0e" },
  { bg: "#f5f3ff", color: "#5b21b6" },
  { bg: "#ecfdf5", color: "#065f46" },
];

function catColor(val, list) {
  const i = list.indexOf(val);
  return CAT_PALETTE[(i >= 0 ? i : 0) % CAT_PALETTE.length];
}

// ── Shared small components ──────────────────────────────────────────────────
function Badge({ label, bg, color }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 12,
      fontSize: "0.77rem", fontWeight: 700, background: bg || "var(--border-color)",
      color: color || C.muted, whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

function StatusBar({ msg, type, onClose }) {
  if (!msg) return null;
  const err = type === "error";
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0.7rem 1rem", borderRadius: 6, marginBottom: "1.25rem",
      background: err ? "#fdf0ef" : "#fdf4ef",
      border: `1px solid ${err ? "#e8b4b0" : "#e8c4a8"}`,
      color: err ? C.red : C.copper, fontSize: "0.9rem",
    }}>
      <span>{msg}</span>
      <button onClick={onClose} style={{
        background: "none", border: "none", cursor: "pointer",
        fontSize: "1.2rem", color: "inherit", lineHeight: 1, padding: "0 4px",
      }}>×</button>
    </div>
  );
}

function Btn({ children, onClick, type = "button", variant = "primary", small, disabled, style: extra }) {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: small ? "0.32rem 0.7rem" : "0.52rem 1.1rem",
    borderRadius: 6, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
    fontSize: small ? "0.8rem" : "0.88rem", border: "none",
    transition: "background 0.15s, opacity 0.15s", opacity: disabled ? 0.55 : 1,
  };
  const v = {
    primary:   { background: C.copper,  color: "#fff" },
    danger:    { background: C.red,    color: "#fff" },
    secondary: { background: "#fff",   color: C.navy,  border: `1px solid ${C.border}` },
    ghost:     { background: "transparent", color: C.muted, border: `1px solid ${C.border}` },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ ...base, ...(v[variant] || v.primary), ...extra }}>
      {children}
    </button>
  );
}

function Modal({ open, onClose, title, width = 540, children }) {
  if (!open) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 9999, padding: "1rem",
    }}>
      <div style={{
        background: C.white, borderRadius: 10, width: "100%", maxWidth: width,
        maxHeight: "92vh", overflowY: "auto",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "1rem 1.25rem 0.8rem",
          borderBottom: `1px solid ${C.border}`,
        }}>
          <h3 style={{ margin: 0, color: C.navy, fontSize: "1.05rem", fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: "1.4rem", color: C.muted, lineHeight: 1, padding: "0 4px",
          }}>×</button>
        </div>
        <div style={{ padding: "1.25rem" }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: "0.9rem" }}>
      <label style={{ display: "block", marginBottom: 4, fontSize: "0.82rem", fontWeight: 600, color: C.muted }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const iStyle = {
  width: "100%", padding: "0.48rem 0.75rem",
  border: `1px solid ${C.border}`, borderRadius: 5,
  fontSize: "0.9rem", boxSizing: "border-box", color: C.text,
  background: "#fff",
};

// ── Main page ────────────────────────────────────────────────────────────────
function TherapistResourcesPage() {
  const [activeTab, setActiveTab] = useState("referrals");
  const [statusMsg, setStatusMsg] = useState("");
  const [statusType, setStatusType] = useState("success");
  const toast = (msg, type = "success") => { setStatusMsg(msg); setStatusType(type); };

  // ── Referrals state ──
  const [referrals, setReferrals] = useState([]);
  const [refSearch, setRefSearch] = useState("");
  const [refCatFilter, setRefCatFilter] = useState("All");
  const [sortField, setSortField] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);

  const [showAddRef, setShowAddRef] = useState(false);
  const [refName, setRefName] = useState("");
  const [refPhone, setRefPhone] = useState("");
  const [refWebsite, setRefWebsite] = useState("");
  const [refAddress, setRefAddress] = useState("");
  const [refPop, setRefPop] = useState("");
  const [refCat, setRefCat] = useState("");
  const [refNotes, setRefNotes] = useState("");

  const [showRefModal, setShowRefModal] = useState(false);
  const [selRef, setSelRef] = useState(null);
  const [editingRef, setEditingRef] = useState(false);
  const [confirmDelRef, setConfirmDelRef] = useState(false);
  const [mRefName, setMRefName] = useState("");
  const [mRefPhone, setMRefPhone] = useState("");
  const [mRefWebsite, setMRefWebsite] = useState("");
  const [mRefAddress, setMRefAddress] = useState("");
  const [mRefPop, setMRefPop] = useState("");
  const [mRefCat, setMRefCat] = useState("");
  const [mRefNotes, setMRefNotes] = useState("");

  // ── Resources state ──
  const [resources, setResources] = useState([]);
  const [resCatFilter, setResCatFilter] = useState("All");
  const [resSearch, setResSearch] = useState("");

  const [showUpload, setShowUpload] = useState(false);
  const [resTitle, setResTitle] = useState("");
  const [resCat, setResCat] = useState("");
  const [resFile, setResFile] = useState(null);
  const [resDesc, setResDesc] = useState("");
  const [resLink, setResLink] = useState("");

  const [showResModal, setShowResModal] = useState(false);
  const [selRes, setSelRes] = useState(null);
  const [editingRes, setEditingRes] = useState(false);
  const [confirmDelRes, setConfirmDelRes] = useState(false);
  const [mResTitle, setMResTitle] = useState("");
  const [mResCat, setMResCat] = useState("");
  const [mResDesc, setMResDesc] = useState("");
  const [mResLink, setMResLink] = useState("");
  const [mResUploader, setMResUploader] = useState("");
  const [mResCreatedAt, setMResCreatedAt] = useState("");
  const [mResFileUrl, setMResFileUrl] = useState("");
  const [mResNewFile, setMResNewFile] = useState(null);

  // ── Static lists ──
  const refCats = [
    "Psychiatrist", "Psychologist", "Psychotherapist", "Social Worker",
    "Substance Abuse Center", "Child/Adolescent Specialist",
    "Couples/Family Therapy", "Legal/Forensic", "Community Resource",
  ];
  const popOpts = [
    "Children", "Adolescents/Teens", "Adults", "Older Adults/Geriatric",
    "Couples", "Families", "All Ages",
  ];
  const resCats = [
    "Clinical Forms & Templates",
    "Practice Policies & Compliance",
    "Client Handouts & Psychoeducation",
    "Professional Development & Training",
    "Marketing & Outreach",
    "Reference Materials & Research",
    "Miscellaneous / General Resources",
  ];
  const API_BASE = "https://www.avenuesportal.com";

  useEffect(() => { fetchReferrals(); fetchResources(); }, []);

  // ── Fetch ──
  async function fetchReferrals() {
    try {
      const r = await API.get("/resources/referrals");
      setReferrals(r.data);
    } catch { toast("Error loading referrals", "error"); }
  }
  async function fetchResources() {
    try {
      const r = await API.get("/resources");
      setResources(r.data);
    } catch { toast("Error loading resources", "error"); }
  }

  // ── Referral actions ──
  async function handleAddReferral(e) {
    e.preventDefault();
    if (!refName.trim()) { toast("Name is required.", "error"); return; }
    try {
      await API.post("/resources/referrals", {
        name: refName, phone: refPhone, website: refWebsite,
        address: refAddress, population: refPop, category: refCat, notes: refNotes,
      });
      toast("Referral added!");
      setRefName(""); setRefPhone(""); setRefWebsite(""); setRefAddress("");
      setRefPop(""); setRefCat(""); setRefNotes(""); setShowAddRef(false);
      fetchReferrals();
    } catch { toast("Error adding referral", "error"); }
  }

  async function handleDeleteReferral(id) {
    try {
      await API.delete(`/resources/referrals/${id}`);
      toast("Referral deleted.");
      fetchReferrals();
      setShowRefModal(false); setSelRef(null); setConfirmDelRef(false);
    } catch { toast("Error deleting referral", "error"); }
  }

  async function handleSaveRef() {
    if (!mRefName.trim()) { toast("Name is required.", "error"); return; }
    try {
      await API.put(`/resources/referrals/${selRef.id}`, {
        name: mRefName, phone: mRefPhone, website: mRefWebsite,
        address: mRefAddress, population: mRefPop, category: mRefCat, notes: mRefNotes,
      });
      toast("Referral updated!");
      fetchReferrals(); setShowRefModal(false); setSelRef(null);
    } catch { toast("Error updating referral", "error"); }
  }

  function openRefModal(r) {
    setSelRef(r); setMRefName(r.name || ""); setMRefPhone(r.phone || "");
    setMRefWebsite(r.website || ""); setMRefAddress(r.address || "");
    setMRefPop(r.population || ""); setMRefCat(r.category || "");
    setMRefNotes(r.notes || ""); setEditingRef(false); setConfirmDelRef(false);
    setShowRefModal(true);
  }

  function handleSort(field) {
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(true); }
  }

  const visibleRefs = [...referrals]
    .filter(r => {
      const q = refSearch.toLowerCase();
      return (
        (refCatFilter === "All" || r.category === refCatFilter) &&
        (!q ||
          (r.name || "").toLowerCase().includes(q) ||
          (r.category || "").toLowerCase().includes(q) ||
          (r.population || "").toLowerCase().includes(q) ||
          (r.notes || "").toLowerCase().includes(q))
      );
    })
    .sort((a, b) => {
      const va = sortField === "dateAdded"
        ? new Date(a.dateAdded)
        : (a[sortField] || "").toLowerCase();
      const vb = sortField === "dateAdded"
        ? new Date(b.dateAdded)
        : (b[sortField] || "").toLowerCase();
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });

  function SortIcon({ field }) {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: 3 }}>↕</span>;
    return <span style={{ marginLeft: 3 }}>{sortAsc ? "↑" : "↓"}</span>;
  }

  // ── Resource actions ──
  async function handleUpload(e) {
    e.preventDefault();
    if (!resTitle.trim() || !resCat) { toast("Title and category are required.", "error"); return; }
    try {
      const fd = new FormData();
      fd.append("title", resTitle); fd.append("category", resCat);
      fd.append("description", resDesc); fd.append("link", resLink);
      if (resFile) fd.append("file", resFile);
      await API.post("/resources/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast("Resource uploaded!");
      setResTitle(""); setResCat(""); setResFile(null); setResDesc(""); setResLink("");
      setShowUpload(false); fetchResources();
    } catch { toast("Error uploading resource", "error"); }
  }

  async function handleDeleteResource(id) {
    try {
      await API.delete(`/resources/${id}`);
      toast("Resource deleted.");
      fetchResources(); setShowResModal(false); setSelRes(null); setConfirmDelRes(false);
    } catch { toast("Error deleting resource", "error"); }
  }

  async function handleSaveRes() {
    if (!mResTitle.trim() || !mResCat.trim()) { toast("Title and category are required.", "error"); return; }
    try {
      const fd = new FormData();
      fd.append("title", mResTitle); fd.append("category", mResCat);
      fd.append("description", mResDesc); fd.append("link", mResLink);
      if (mResNewFile) fd.append("file", mResNewFile);
      await API.put(`/resources/${selRes.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast("Resource updated!"); fetchResources(); setShowResModal(false); setSelRes(null);
    } catch { toast("Error updating resource", "error"); }
  }

  function openResModal(r) {
    setSelRes(r); setMResTitle(r.title || ""); setMResCat(r.category || "");
    setMResDesc(r.description || ""); setMResLink(r.link || "");
    setMResFileUrl(r.file_url || ""); setMResUploader(r.uploader_name || "");
    setMResCreatedAt(r.created_at || ""); setMResNewFile(null);
    setEditingRes(false); setConfirmDelRes(false); setShowResModal(true);
  }

  const visibleRes = resources.filter(r => {
    const q = resSearch.toLowerCase();
    return (
      (resCatFilter === "All" || r.category === resCatFilter) &&
      (!q ||
        (r.title || "").toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q) ||
        (r.category || "").toLowerCase().includes(q))
    );
  });

  // ── Category pill row ──
  function CatPills({ list, active, setActive }) {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: "1.1rem" }}>
        {["All", ...list].map(c => (
          <button key={c} onClick={() => setActive(c)} style={{
            padding: "4px 12px", borderRadius: 20, fontSize: "0.78rem", fontWeight: 600,
            border: `1px solid ${active === c ? C.copper : C.border}`,
            background: active === c ? C.copper : "#fff",
            color: active === c ? "#fff" : C.muted,
            cursor: "pointer", transition: "all 0.13s",
          }}>{c}</button>
        ))}
      </div>
    );
  }

  // ── Render ──
  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "1.5rem 1rem" }}>

      {/* Page header */}
      <div style={{ marginBottom: "1.25rem" }}>
        <h1 style={{ margin: 0, color: C.navy, fontSize: "1.6rem", fontWeight: 700 }}>Resources</h1>
        <p style={{ margin: "0.2rem 0 0", color: C.muted, fontSize: "0.88rem" }}>
          Referral network and practice resource library
        </p>
      </div>

      <StatusBar msg={statusMsg} type={statusType} onClose={() => setStatusMsg("")} />

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 4, borderBottom: `2px solid ${C.border}`, marginBottom: "1.5rem" }}>
        {[["referrals", "Referral List"], ["resources", "Resource Library"]].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{
            padding: "0.58rem 1.25rem", fontSize: "0.93rem", fontWeight: 700,
            border: "none", borderRadius: "6px 6px 0 0", cursor: "pointer",
            marginBottom: -2, transition: "all 0.13s",
            background: activeTab === id ? C.copper : "transparent",
            color: activeTab === id ? "#fff" : C.muted,
            borderBottom: activeTab === id ? `2px solid ${C.copper}` : "none",
          }}>{label}</button>
        ))}
      </div>

      {/* ══════════════ REFERRALS ══════════════ */}
      {activeTab === "referrals" && (
        <>
          {/* Toolbar */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.7rem", alignItems: "center", marginBottom: "1rem" }}>
            <input
              type="text" placeholder="Search referrals…"
              value={refSearch} onChange={e => setRefSearch(e.target.value)}
              style={{ ...iStyle, maxWidth: 300, flex: "1 1 180px" }}
            />
            <Btn onClick={() => setShowAddRef(true)}>+ Add Referral</Btn>
          </div>

          <CatPills list={refCats} active={refCatFilter} setActive={setRefCatFilter} />

          {/* Table */}
          <div style={{ background: C.white, borderRadius: 8, boxShadow: C.shadow, overflowX: "auto" }}>
            {visibleRefs.length === 0 ? (
              <p style={{ padding: "2.5rem", textAlign: "center", color: C.muted, margin: 0 }}>
                {refSearch || refCatFilter !== "All"
                  ? "No referrals match your search."
                  : "No referrals yet — click + Add Referral to get started."}
              </p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.87rem", minWidth: 500 }}>
                <thead>
                  <tr style={{ background: "#f8f9fb" }}>
                    {[["name","Name"],["category","Category"],["phone","Phone"],["population","Population"]].map(([f,l]) => (
                      <th key={f} onClick={() => handleSort(f)} style={{
                        padding: "10px 14px", textAlign: "left", fontWeight: 600,
                        fontSize: "0.79rem", color: C.muted, cursor: "pointer",
                        borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap", userSelect: "none",
                      }}>
                        {l}<SortIcon field={f} />
                      </th>
                    ))}
                    <th style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, width: 70 }} />
                  </tr>
                </thead>
                <tbody>
                  {visibleRefs.map((r, i) => {
                    const cc = catColor(r.category, refCats);
                    return (
                      <tr key={r.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                        <td style={{ padding: "10px 14px", fontWeight: 600, color: C.text }}>{r.name || "—"}</td>
                        <td style={{ padding: "10px 14px" }}>
                          {r.category ? <Badge label={r.category} bg={cc.bg} color={cc.color} /> : "—"}
                        </td>
                        <td style={{ padding: "10px 14px", color: C.muted }}>{r.phone || "—"}</td>
                        <td style={{ padding: "10px 14px", color: C.muted }}>{r.population || "—"}</td>
                        <td style={{ padding: "10px 14px", textAlign: "right" }}>
                          <Btn small variant="secondary" onClick={() => openRefModal(r)}>View</Btn>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <p style={{ fontSize: "0.78rem", color: C.muted, marginTop: "0.4rem" }}>
            {visibleRefs.length} referral{visibleRefs.length !== 1 ? "s" : ""}
          </p>
        </>
      )}

      {/* ══════════════ RESOURCES ══════════════ */}
      {activeTab === "resources" && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.7rem", alignItems: "center", marginBottom: "1rem" }}>
            <input
              type="text" placeholder="Search resources…"
              value={resSearch} onChange={e => setResSearch(e.target.value)}
              style={{ ...iStyle, maxWidth: 300, flex: "1 1 180px" }}
            />
            <Btn onClick={() => setShowUpload(true)}>+ Upload Resource</Btn>
          </div>

          <CatPills list={resCats} active={resCatFilter} setActive={setResCatFilter} />

          {visibleRes.length === 0 ? (
            <div style={{
              padding: "3rem", textAlign: "center", color: C.muted,
              background: C.white, borderRadius: 8, boxShadow: C.shadow,
            }}>
              {resSearch || resCatFilter !== "All"
                ? "No resources match your search."
                : "No resources yet — click + Upload Resource to add one."}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: "1rem" }}>
              {visibleRes.map(r => {
                const cc = catColor(r.category, resCats);
                return (
                  <div key={r.id} style={{
                    background: C.white, borderRadius: 8, boxShadow: C.shadow,
                    padding: "1rem 1.1rem", display: "flex", flexDirection: "column",
                    gap: 8, border: `1px solid ${C.border}`,
                  }}>
                    <div>
                      <Badge label={r.category} bg={cc.bg} color={cc.color} />
                    </div>
                    <div style={{ fontWeight: 700, fontSize: "0.95rem", color: C.text, lineHeight: 1.3 }}>
                      {r.title}
                    </div>
                    {r.description && (
                      <div style={{
                        fontSize: "0.82rem", color: C.muted, lineHeight: 1.45,
                        overflow: "hidden", display: "-webkit-box",
                        WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                      }}>
                        {r.description}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 6, marginTop: "auto", flexWrap: "wrap", paddingTop: 4 }}>
                      {r.file_url && (
                        <a href={`${API_BASE}${r.file_url}`} target="_blank" rel="noreferrer" style={{
                          padding: "4px 10px", borderRadius: 5, fontSize: "0.78rem", fontWeight: 600,
                          background: "#e8f4fd", color: "#1a6fa8", textDecoration: "none",
                          border: "1px solid #b8d9f0",
                        }}>
                          📄 Open File
                        </a>
                      )}
                      {r.link && (
                        <a href={r.link} target="_blank" rel="noreferrer" style={{
                          padding: "4px 10px", borderRadius: 5, fontSize: "0.78rem", fontWeight: 600,
                          background: "#f0fdf4", color: "#15803d", textDecoration: "none",
                          border: "1px solid #a7d3b8",
                        }}>
                          🔗 Visit Link
                        </a>
                      )}
                      <Btn small variant="ghost" onClick={() => openResModal(r)}>Details</Btn>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p style={{ fontSize: "0.78rem", color: C.muted, marginTop: "0.6rem" }}>
            {visibleRes.length} resource{visibleRes.length !== 1 ? "s" : ""}
          </p>
        </>
      )}

      {/* ══════════════ ADD REFERRAL MODAL ══════════════ */}
      <Modal open={showAddRef} onClose={() => setShowAddRef(false)} title="Add New Referral" width={560}>
        <form onSubmit={handleAddReferral}>
          <Field label="Name / Practice *">
            <input style={iStyle} type="text" value={refName}
              onChange={e => setRefName(e.target.value)} placeholder="e.g. Dr. Jane Smith, LCSW" />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <Field label="Phone">
              <input style={iStyle} type="text" value={refPhone}
                onChange={e => setRefPhone(e.target.value)} placeholder="(555) 555-5555" />
            </Field>
            <Field label="Category">
              <select style={iStyle} value={refCat} onChange={e => setRefCat(e.target.value)}>
                <option value="">— Select —</option>
                {refCats.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Population Served">
              <select style={iStyle} value={refPop} onChange={e => setRefPop(e.target.value)}>
                <option value="">— Select —</option>
                {popOpts.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Website">
              <input style={iStyle} type="url" value={refWebsite}
                onChange={e => setRefWebsite(e.target.value)} placeholder="https://…" />
            </Field>
          </div>
          <Field label="Address">
            <input style={iStyle} type="text" value={refAddress} onChange={e => setRefAddress(e.target.value)} />
          </Field>
          <Field label="Notes">
            <textarea style={{ ...iStyle, height: 80, resize: "vertical" }}
              value={refNotes} onChange={e => setRefNotes(e.target.value)} />
          </Field>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={() => setShowAddRef(false)}>Cancel</Btn>
            <Btn type="submit">Add Referral</Btn>
          </div>
        </form>
      </Modal>

      {/* ══════════════ VIEW / EDIT REFERRAL MODAL ══════════════ */}
      <Modal
        open={showRefModal && !!selRef}
        onClose={() => { setShowRefModal(false); setSelRef(null); setEditingRef(false); setConfirmDelRef(false); }}
        title={editingRef ? "Edit Referral" : (mRefName || "Referral Details")}
        width={540}
      >
        {confirmDelRef ? (
          <>
            <p style={{ color: C.text, marginBottom: "1rem" }}>
              Delete <strong>{mRefName}</strong>? This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setConfirmDelRef(false)}>Cancel</Btn>
              <Btn variant="danger" onClick={() => handleDeleteReferral(selRef.id)}>Delete</Btn>
            </div>
          </>
        ) : !editingRef ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.9rem", marginBottom: "1rem" }}>
              {[
                ["CATEGORY", mRefCat ? <Badge label={mRefCat} {...catColor(mRefCat, refCats)} /> : "—"],
                ["POPULATION", mRefPop || "—"],
                ["PHONE", mRefPhone || "—"],
                ["DATE ADDED", selRef?.dateAdded ? new Date(selRef.dateAdded).toLocaleDateString() : "—"],
              ].map(([lbl, val]) => (
                <div key={lbl}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.muted, marginBottom: 3 }}>{lbl}</div>
                  <div style={{ fontSize: "0.9rem", color: C.text }}>{val}</div>
                </div>
              ))}
            </div>
            {mRefWebsite && (
              <div style={{ marginBottom: "0.75rem" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.muted, marginBottom: 3 }}>WEBSITE</div>
                <a href={mRefWebsite} target="_blank" rel="noreferrer"
                  style={{ fontSize: "0.9rem", color: C.copper }}>{mRefWebsite}</a>
              </div>
            )}
            {mRefAddress && (
              <div style={{ marginBottom: "0.75rem" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.muted, marginBottom: 3 }}>ADDRESS</div>
                <span style={{ fontSize: "0.9rem" }}>{mRefAddress}</span>
              </div>
            )}
            {mRefNotes && (
              <div style={{ marginBottom: "0.75rem" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.muted, marginBottom: 4 }}>NOTES</div>
                <p style={{
                  margin: 0, fontSize: "0.88rem", background: "#f8f9fb",
                  padding: "0.5rem 0.75rem", borderRadius: 5, border: `1px solid ${C.border}`,
                  lineHeight: 1.5,
                }}>{mRefNotes}</p>
              </div>
            )}
            <div style={{
              display: "flex", gap: 8, justifyContent: "flex-end",
              marginTop: "1rem", paddingTop: "0.75rem", borderTop: `1px solid ${C.border}`,
            }}>
              <Btn small variant="danger" onClick={() => setConfirmDelRef(true)}>Delete</Btn>
              <Btn variant="secondary" onClick={() => setEditingRef(true)}>Edit</Btn>
            </div>
          </>
        ) : (
          <>
            <Field label="Name / Practice *">
              <input style={iStyle} type="text" value={mRefName} onChange={e => setMRefName(e.target.value)} />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <Field label="Phone">
                <input style={iStyle} type="text" value={mRefPhone} onChange={e => setMRefPhone(e.target.value)} />
              </Field>
              <Field label="Category">
                <select style={iStyle} value={mRefCat} onChange={e => setMRefCat(e.target.value)}>
                  <option value="">— Select —</option>
                  {refCats.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Population Served">
                <select style={iStyle} value={mRefPop} onChange={e => setMRefPop(e.target.value)}>
                  <option value="">— Select —</option>
                  {popOpts.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Website">
                <input style={iStyle} type="text" value={mRefWebsite} onChange={e => setMRefWebsite(e.target.value)} />
              </Field>
            </div>
            <Field label="Address">
              <input style={iStyle} type="text" value={mRefAddress} onChange={e => setMRefAddress(e.target.value)} />
            </Field>
            <Field label="Notes">
              <textarea style={{ ...iStyle, height: 80, resize: "vertical" }}
                value={mRefNotes} onChange={e => setMRefNotes(e.target.value)} />
            </Field>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setEditingRef(false)}>Cancel</Btn>
              <Btn onClick={handleSaveRef}>Save Changes</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* ══════════════ UPLOAD RESOURCE MODAL ══════════════ */}
      <Modal open={showUpload} onClose={() => setShowUpload(false)} title="Upload New Resource" width={560}>
        <form onSubmit={handleUpload} encType="multipart/form-data">
          <Field label="Title *">
            <input style={iStyle} type="text" value={resTitle}
              onChange={e => setResTitle(e.target.value)} placeholder="Resource title" />
          </Field>
          <Field label="Category *">
            <select style={iStyle} value={resCat} onChange={e => setResCat(e.target.value)}>
              <option value="">— Choose Category —</option>
              {resCats.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Description">
            <textarea style={{ ...iStyle, height: 80, resize: "vertical" }}
              value={resDesc} onChange={e => setResDesc(e.target.value)}
              placeholder="Brief description of this resource…" />
          </Field>
          <Field label="External Link (optional)">
            <input style={iStyle} type="url" value={resLink}
              onChange={e => setResLink(e.target.value)} placeholder="https://…" />
          </Field>
          <Field label="File (optional — PDF, Word, image)">
            <input type="file" onChange={e => setResFile(e.target.files[0])} style={{ fontSize: "0.88rem" }}
              accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" />
          </Field>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={() => setShowUpload(false)}>Cancel</Btn>
            <Btn type="submit">Upload</Btn>
          </div>
        </form>
      </Modal>

      {/* ══════════════ VIEW / EDIT RESOURCE MODAL ══════════════ */}
      <Modal
        open={showResModal && !!selRes}
        onClose={() => { setShowResModal(false); setSelRes(null); setEditingRes(false); setConfirmDelRes(false); }}
        title={editingRes ? "Edit Resource" : (mResTitle || "Resource Details")}
        width={540}
      >
        {confirmDelRes ? (
          <>
            <p style={{ color: C.text, marginBottom: "1rem" }}>
              Delete <strong>{mResTitle}</strong>? This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setConfirmDelRes(false)}>Cancel</Btn>
              <Btn variant="danger" onClick={() => handleDeleteResource(selRes.id)}>Delete</Btn>
            </div>
          </>
        ) : !editingRes ? (
          <>
            {mResCat && (
              <div style={{ marginBottom: "0.75rem" }}>
                <Badge label={mResCat} {...catColor(mResCat, resCats)} />
              </div>
            )}
            {mResDesc && (
              <div style={{ marginBottom: "1rem" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.muted, marginBottom: 4 }}>DESCRIPTION</div>
                <p style={{ margin: 0, fontSize: "0.9rem", lineHeight: 1.55, color: C.text }}>{mResDesc}</p>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
              {[
                ["UPLOADED BY", mResUploader || "—"],
                ["DATE", mResCreatedAt ? new Date(mResCreatedAt).toLocaleDateString() : "—"],
              ].map(([lbl, val]) => (
                <div key={lbl}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.muted, marginBottom: 3 }}>{lbl}</div>
                  <div style={{ fontSize: "0.9rem", color: C.text }}>{val}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: "1rem" }}>
              {mResFileUrl && (
                <a href={`${API_BASE}${mResFileUrl}`} target="_blank" rel="noreferrer" style={{
                  padding: "6px 14px", borderRadius: 5, fontSize: "0.86rem", fontWeight: 600,
                  background: "#e8f4fd", color: "#1a6fa8", textDecoration: "none", border: "1px solid #b8d9f0",
                }}>📄 Open File</a>
              )}
              {mResLink && (
                <a href={mResLink} target="_blank" rel="noreferrer" style={{
                  padding: "6px 14px", borderRadius: 5, fontSize: "0.86rem", fontWeight: 600,
                  background: "#f0fdf4", color: "#15803d", textDecoration: "none", border: "1px solid #a7d3b8",
                }}>🔗 Visit Link</a>
              )}
            </div>
            <div style={{
              display: "flex", gap: 8, justifyContent: "flex-end",
              paddingTop: "0.75rem", borderTop: `1px solid ${C.border}`,
            }}>
              <Btn small variant="danger" onClick={() => setConfirmDelRes(true)}>Delete</Btn>
              <Btn variant="secondary" onClick={() => setEditingRes(true)}>Edit</Btn>
            </div>
          </>
        ) : (
          <>
            <Field label="Title *">
              <input style={iStyle} type="text" value={mResTitle} onChange={e => setMResTitle(e.target.value)} />
            </Field>
            <Field label="Category *">
              <select style={iStyle} value={mResCat} onChange={e => setMResCat(e.target.value)}>
                <option value="">— Choose Category —</option>
                {resCats.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Description">
              <textarea style={{ ...iStyle, height: 80, resize: "vertical" }}
                value={mResDesc} onChange={e => setMResDesc(e.target.value)} />
            </Field>
            <Field label="External Link">
              <input style={iStyle} type="url" value={mResLink} onChange={e => setMResLink(e.target.value)} />
            </Field>
            <Field label="Replace File (optional)">
              <input type="file" onChange={e => setMResNewFile(e.target.files[0])} style={{ fontSize: "0.88rem" }}
                accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" />
              <p style={{ margin: "4px 0 0", fontSize: "0.76rem", color: C.muted }}>
                Leave blank to keep the existing file.
              </p>
            </Field>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setEditingRes(false)}>Cancel</Btn>
              <Btn onClick={handleSaveRes}>Save Changes</Btn>
            </div>
          </>
        )}
      </Modal>

    </div>
  );
}

export default TherapistResourcesPage;
